// The file, not the `~/features/auth` barrel — that barrel pulls in the auth component index, which
// includes `OnboardingTaskList`, itself a cashier entry point.
import { useUser as useViewer } from '~/features/auth/api/useUser'
import { useSettings } from '~/features/settings'

/**
 * Whether each half of the cashier is open, and why not when it is shut.
 *
 * Deliberately holds no JSX and imports no component: `useCashier` needs the modal components, and
 * `DepositWithdraw` — which is inside those components — needs these same answers for its withdraw
 * tab. Putting the rules in the hook that opens the modal would close that loop into an import
 * cycle, so the rules live here and both sides read them.
 */
export const useBankGates = () => {
    const { data: viewer } = useViewer()
    const { data: settings } = useSettings()

    const emailConfirmed = !!viewer?.email_confirmed

    // A staff hold on this account. The server refuses the spend either way -- this is here so
    // the player is told what happened rather than left with a balance of zero and a button
    // that does nothing.
    const fundsFrozen = !!viewer?.funds_frozen
    const frozenReason = 'Your funds are on hold. Contact support.'

    /*
      Prose rather than a boolean. Callers have to say *which* condition is missing — a disabled
      button with no explanation is the thing this replaced — and a caller that only needs a yes/no
      can still read it as one. Returning `true` would make every call site re-derive the wording,
      which is how the header and the dashboard drifted apart in the first place.
    */
    const withdrawBlocked = fundsFrozen
        ? frozenReason
        : !settings?.WITHDRAWAL_ENABLED
          ? 'Withdrawals are temporarily disabled'
          : !emailConfirmed
            ? 'Confirm your email address to withdraw'
            : undefined

    // Separate from `withdrawBlocked` because the store has none of the other conditions: a
    // withdrawal can be shut for reasons that have nothing to do with the player, and spending is
    // only ever stopped by a hold.
    const spendBlocked = fundsFrozen ? frozenReason : undefined

    return { emailConfirmed, fundsFrozen, withdrawBlocked, spendBlocked }
}
